import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Plus, DollarSign, Percent, Calculator, Gift, Wallet, Layers, Pencil, Trash2, Save, Info, Landmark } from 'lucide-react';
import { Tabs, useUrlTab } from '../../components/ui';
import { useConfirm } from '../../components/ConfirmDialog';
import GovFigures from './GovFigures';
import ProviderRules from './ProviderRules';
import { isoDate } from '../../utils/time';
import {
  PageHead, Panel, Register, DataTable, FigureStrip, Figure, DetailDrawer, Currency, Field, FormRow,
  fmtDate, money,
} from './design';

const today = () => isoDate();
const ymd = (d) => (d ? String(d).slice(0, 10) : '');
const schemeOf = (s) => (String(s || 'SAH').toUpperCase() === 'HCP' ? 'HCP' : 'SAH');

const SUPPLEMENT_OPTIONS = [
  'ARIA Remoteness',
  'Viability',
  'Dementia & Cognition',
  'Oxygen',
  'Enteral Feeding',
  'Hardship',
  'Veterans',
];

const blankClass = () => ({ scheme: 'SAH', level: '', rate: '', effective_date: today() });
const blankSupp = () => ({ name: SUPPLEMENT_OPTIONS[0], type: 'Supplement', amount: '', effective_date: today() });
// A new fee starts on Home Care Package: Support at Home has no care or package management fee on
// the budget, and the server refuses one.
const blankFee = () => ({ scheme: 'HCP', level: '', care_mode: 'percent', care_value: '', package_mode: 'percent', package_value: '', effective_date: today() });
const EMPTY_CONTRIB = { rates: [], lifetimeCap: 0, personalCareFreeFrom: '' };

// Row actions sit inside a clickable row: without this a click on Remove would also open the row,
// and Enter on the button would be taken by the row.
const stop = (e) => e.stopPropagation();

// Two schemes run side by side: Support at Home, and the Home Care Packages it replaced. Which one
// a rate belongs to changes how it is paid, so it is said in the product's own badge rather than a
// pill invented here.
function Scheme({ scheme }) {
  if (!scheme) return <span className="text-slate-300">—</span>;
  const sah = String(scheme).toUpperCase() === 'SAH';
  return <span className={sah ? 'badge badge-blue' : 'badge badge-gray'}>{sah ? 'Support at Home' : 'Home Care Package'}</span>;
}

// A two-state toggle for "is this fee a percentage or a dollar amount". Kept as a segmented control
// because the choice changes what the number beside it means.
function ModeToggle({ value, onChange, label }) {
  return (
    <div className="flex rounded-lg border border-slate-200 overflow-hidden shrink-0" role="group" aria-label={label}>
      <button
        type="button" aria-pressed={value === 'percent'} onClick={() => onChange('percent')}
        className={`flex items-center gap-1 px-3 py-2 text-[13px] ${value === 'percent' ? 'bg-primary-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
      >
        <Percent size={14} /> Percent
      </button>
      <button
        type="button" aria-pressed={value === 'amount'} onClick={() => onChange('amount')}
        className={`flex items-center gap-1 px-3 py-2 text-[13px] ${value === 'amount' ? 'bg-primary-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
      >
        <DollarSign size={14} /> Dollars
      </button>
    </div>
  );
}

const MEANS = [
  { key: 'full_pensioner', label: 'Full pensioner' },
  { key: 'part_pensioner', label: 'Part pensioner' },
  { key: 'self_funded', label: 'Self-funded' },
];
const CONTRIB_ROWS = [
  { cat: 'clinical', label: 'Clinical supports' },
  { cat: 'independence', label: 'Independence' },
  { cat: 'everyday', label: 'Everyday living' },
];

// Your own rates first; the contribution table and the government's published figures (56 rows of
// reference) each on their own tab, so the rates you edit are not buried under them.
const VIEWS = ['yours', 'contributions', 'government'];

export default function AgedCareRates() {
  const confirm = useConfirm();
  const [view, setView] = useUrlTab('view', 'yours', VIEWS);
  const [loading, setLoading] = useState(true);
  // The page-wide skeleton is for the first load only; a retry shows each list's own skeleton so
  // the government figures and rules above are not thrown away and fetched again.
  const [loaded, setLoaded] = useState(false);
  const [classifications, setClassifications] = useState([]);
  const [supplements, setSupplements] = useState([]);
  const [fees, setFees] = useState([]);
  // One message per list: a list that failed to load says so, rather than reading as empty.
  const [errors, setErrors] = useState({});

  const [showClass, setShowClass] = useState(false);
  const [showSupp, setShowSupp] = useState(false);
  const [showFee, setShowFee] = useState(false);
  // The row open for editing in each drawer, or null while adding a new one.
  const [editingClass, setEditingClass] = useState(null);
  const [editingSupp, setEditingSupp] = useState(null);
  const [editingFee, setEditingFee] = useState(null);
  // Stable, or each drawer re-runs its focus effect on every keystroke in its form and pulls focus
  // out of the field being typed in.
  const closeClass = useCallback(() => setShowClass(false), []);
  const closeSupp = useCallback(() => setShowSupp(false), []);
  const closeFee = useCallback(() => setShowFee(false), []);
  const [saving, setSaving] = useState(false);
  const [contrib, setContrib] = useState(EMPTY_CONTRIB);

  const load = useCallback(async () => {
    setLoading(true);
    const [c, s, f, cr] = await Promise.allSettled([
      api.get('/aged-care/classifications'),
      api.get('/aged-care/supplements'),
      api.get('/aged-care/fees'),
      api.get('/aged-care/contribution-rates'),
    ]);
    const list = (r) => (r.status === 'fulfilled' ? (Array.isArray(r.value.data) ? r.value.data : (r.value.data?.items || [])) : []);
    const fail = (r, fallback) => (r.status === 'rejected' ? (r.reason?.response?.data?.message || fallback) : null);
    setClassifications(list(c));
    setSupplements(list(s));
    setFees(list(f));
    setContrib(cr.status === 'fulfilled' && cr.value.data && cr.value.data.rates ? cr.value.data : EMPTY_CONTRIB);
    setErrors({
      classifications: fail(c, 'Could not load the subsidy rates'),
      supplements: fail(s, 'Could not load the supplements'),
      fees: fail(f, 'Could not load the management fees'),
      contrib: fail(cr, 'Could not load the contribution rates'),
    });
    setLoading(false);
    setLoaded(true);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ---- Classification form ----
  const [classForm, setClassForm] = useState(blankClass());
  const openClass = (row) => {
    setEditingClass(row || null);
    setClassForm(row
      ? { scheme: schemeOf(row.scheme), level: row.level || row.classification || '', rate: String(row.rate ?? row.amount ?? ''), effective_date: ymd(row.effective_date) }
      : blankClass());
    setShowClass(true);
  };
  const submitClass = async () => {
    if (!classForm.level.trim() || !classForm.rate) {
      toast.error('Enter the classification or level, and the rate');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        scheme: classForm.scheme,
        level: classForm.level.trim(),
        rate: Number(classForm.rate),
        period: classForm.scheme === 'SAH' ? 'quarterly' : 'daily',
        effective_date: classForm.effective_date || null,
      };
      if (editingClass) {
        const res = await api.put(`/aged-care/classifications/${editingClass.id}`, payload);
        setClassifications(prev => prev.map(x => (x.id === editingClass.id ? { ...x, ...payload, ...(res.data || {}) } : x)));
        toast.success('Subsidy rate saved');
      } else {
        const res = await api.post('/aged-care/classifications', payload);
        if (res.data?.id) setClassifications(prev => [res.data, ...prev]); else load();
        toast.success('Subsidy rate added');
      }
      setShowClass(false);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not save the subsidy rate');
    } finally {
      setSaving(false);
    }
  };

  // ---- Supplement form ----
  const [suppForm, setSuppForm] = useState(blankSupp());
  const openSupp = (row) => {
    setEditingSupp(row || null);
    setSuppForm(row
      ? { name: row.name || '', type: row.type || 'Supplement', amount: String(row.amount ?? row.default_amount ?? ''), effective_date: ymd(row.effective_date) }
      : blankSupp());
    setShowSupp(true);
  };
  const submitSupp = async () => {
    if (!suppForm.name.trim() || !suppForm.amount) {
      toast.error('Enter the name and the default amount');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: suppForm.name.trim(),
        type: suppForm.type,
        amount: Number(suppForm.amount),
        effective_date: suppForm.effective_date || null,
      };
      if (editingSupp) {
        const res = await api.put(`/aged-care/supplements/${editingSupp.id}`, payload);
        setSupplements(prev => prev.map(x => (x.id === editingSupp.id ? { ...x, ...payload, ...(res.data || {}) } : x)));
        toast.success('Supplement saved');
      } else {
        const res = await api.post('/aged-care/supplements', payload);
        if (res.data?.id) setSupplements(prev => [res.data, ...prev]); else load();
        toast.success('Supplement added');
      }
      setShowSupp(false);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not save the supplement');
    } finally {
      setSaving(false);
    }
  };

  // ---- Fee form ----
  const [feeForm, setFeeForm] = useState(blankFee());
  const openFee = (row) => {
    setEditingFee(row || null);
    setFeeForm(row
      ? {
        scheme: schemeOf(row.scheme),
        level: row.level || row.classification || '',
        care_mode: row.care_mgmt_mode === 'amount' ? 'amount' : 'percent',
        care_value: row.care_mgmt_value == null ? '' : String(row.care_mgmt_value),
        package_mode: row.package_mgmt_mode === 'amount' ? 'amount' : 'percent',
        package_value: row.package_mgmt_value == null ? '' : String(row.package_mgmt_value),
        effective_date: ymd(row.effective_date),
      }
      : blankFee());
    setShowFee(true);
  };
  const feeIsSah = feeForm.scheme === 'SAH';
  const submitFee = async () => {
    if (feeIsSah) {
      toast.error('Support at Home has no care or package management fee to set');
      return;
    }
    if (!feeForm.level.trim() || (!feeForm.care_value && !feeForm.package_value)) {
      toast.error('Enter the classification or level, and at least one fee');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        scheme: feeForm.scheme,
        level: feeForm.level.trim(),
        care_mgmt_mode: feeForm.care_mode,
        care_mgmt_value: feeForm.care_value === '' ? null : Number(feeForm.care_value),
        package_mgmt_mode: feeForm.package_mode,
        package_mgmt_value: feeForm.package_value === '' ? null : Number(feeForm.package_value),
        effective_date: feeForm.effective_date || null,
      };
      if (editingFee) {
        const res = await api.put(`/aged-care/fees/${editingFee.id}`, payload);
        setFees(prev => prev.map(x => (x.id === editingFee.id ? { ...x, ...payload, ...(res.data || {}) } : x)));
        toast.success('Management fee saved');
      } else {
        const res = await api.post('/aged-care/fees', payload);
        if (res.data?.id) setFees(prev => [res.data, ...prev]); else load();
        toast.success('Management fee added');
      }
      setShowFee(false);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not save the management fee');
    } finally {
      setSaving(false);
    }
  };

  // ---- Remove ----
  // For a row added by mistake. A rate that changes gets a new row from its date instead, so the
  // services before that date keep the figure they were priced with; the question says so.
  const REMOVE = {
    classifications: { path: 'classifications', set: setClassifications, noun: 'rate', name: (r) => r.level || r.classification },
    supplements: { path: 'supplements', set: setSupplements, noun: 'supplement', name: (r) => r.name },
    fees: { path: 'fees', set: setFees, noun: 'fee', name: (r) => r.level || r.classification },
  };
  const removeRow = async (kind, row) => {
    const cfg = REMOVE[kind];
    const name = cfg.name(row);
    const ok = await confirm(
      `Use this for a ${cfg.noun} added by mistake. When a figure changes, add a new row from the date it applies instead, so earlier services keep the figure they had.`,
      { title: name ? `Remove the ${name} ${cfg.noun}?` : `Remove this ${cfg.noun}?`, confirmLabel: `Remove ${cfg.noun}`, danger: true },
    );
    if (!ok) return;
    try {
      await api.delete(`/aged-care/${cfg.path}/${row.id}`);
      cfg.set(prev => prev.filter(x => x.id !== row.id));
      toast.success(`${cfg.noun.charAt(0).toUpperCase()}${cfg.noun.slice(1)} removed`);
    } catch (e) {
      toast.error(e.response?.data?.message || `Could not remove the ${cfg.noun}`);
    }
  };
  const actionsFor = (kind, open) => (row) => (
    <span className="flex items-center gap-1" onClick={stop} onKeyDown={stop}>
      <button type="button" onClick={() => open(row)} className="btn-ghost btn-sm"><Pencil size={13} /> Edit</button>
      <button type="button" onClick={() => removeRow(kind, row)} className="btn-ghost btn-sm text-slate-500"><Trash2 size={13} /> Remove</button>
    </span>
  );

  const renderFeeCell = (mode, value) => {
    if (value === null || value === undefined || value === '') return <span className="text-slate-300">—</span>;
    return mode === 'percent' ? <span className="tabular-nums">{Number(value)}%</span> : <Currency value={value} />;
  };

  const head = (
    <PageHead icon={Calculator}
      title="Rates and fees"
      purpose="What the government pays you, what you may charge on top, and what the participant contributes. Every figure here is resolved by service date: when a rate changes, add a row from the date it applies so earlier services keep the rate they had. Edit a row only to correct a mistake in it."
    />
  );

  if (loading && !loaded) {
    return (
      <div>
        {head}
        <div className="space-y-5" aria-busy="true" aria-label="Loading">
          <div className="skeleton h-20 rounded-xl" />
          <div className="skeleton h-56 rounded-xl" />
          <div className="skeleton h-56 rounded-xl" />
        </div>
      </div>
    );
  }

  const usingShipped = contrib.capsSource === 'shipped-default';

  const classColumns = [
    { key: 'scheme', header: 'Scheme', width: 190, sortable: true, render: (c) => <Scheme scheme={c.scheme} /> },
    {
      key: 'level', header: 'Classification or level', truncate: true, sortable: true,
      sortValue: (c) => c.level || c.classification || '',
      render: (c) => <span className="font-medium text-slate-800">{c.level || c.classification || '—'}</span>,
    },
    {
      key: 'rate', header: 'Rate', width: 130, align: 'right', sortable: true, defaultDir: 'desc',
      sortValue: (c) => Number(c.rate ?? c.amount ?? 0),
      render: (c) => <Currency value={c.rate ?? c.amount} className="font-medium text-slate-900" />,
    },
    {
      key: 'period', header: 'Paid', width: 120, sortable: true,
      sortValue: (c) => c.period || (c.scheme === 'SAH' ? 'quarterly' : 'daily'),
      render: (c) => <span className="text-slate-600">{(c.period || (c.scheme === 'SAH' ? 'quarterly' : 'daily')) === 'quarterly' ? 'Each quarter' : 'Each day'}</span>,
    },
    {
      key: 'effective_date', header: 'Effective from', width: 150, sortable: true,
      render: (c) => <span className="text-slate-500 tabular-nums">{fmtDate(c.effective_date)}</span>,
    },
  ];

  const suppColumns = [
    { key: 'name', header: 'Supplement', truncate: true, sortable: true, render: (s) => <span className="font-medium text-slate-800">{s.name || '—'}</span> },
    { key: 'type', header: 'Type', width: 140, sortable: true, render: (s) => <span className="badge badge-gray">{s.type || 'Supplement'}</span> },
    {
      key: 'amount', header: 'Default amount', width: 160, align: 'right', sortable: true, defaultDir: 'desc',
      sortValue: (s) => Number(s.amount ?? s.default_amount ?? 0),
      render: (s) => <Currency value={s.amount ?? s.default_amount} className="font-medium text-slate-900" />,
    },
    {
      key: 'effective_date', header: 'Effective from', width: 150, sortable: true,
      render: (s) => <span className="text-slate-500 tabular-nums">{fmtDate(s.effective_date)}</span>,
    },
  ];

  const feeColumns = [
    { key: 'scheme', header: 'Scheme', width: 190, sortable: true, render: (f) => <Scheme scheme={f.scheme} /> },
    {
      key: 'level', header: 'Classification or level', truncate: true, sortable: true,
      sortValue: (f) => f.level || f.classification || '',
      render: (f) => <span className="font-medium text-slate-800">{f.level || f.classification || '—'}</span>,
    },
    {
      key: 'care_mgmt_value', header: 'Care management', width: 160, align: 'right', sortable: true,
      render: (f) => renderFeeCell(f.care_mgmt_mode, f.care_mgmt_value),
    },
    {
      key: 'package_mgmt_value', header: 'Package management', width: 180, align: 'right', sortable: true,
      render: (f) => renderFeeCell(f.package_mgmt_mode, f.package_mgmt_value),
    },
    {
      key: 'effective_date', header: 'Effective from', width: 150, sortable: true,
      render: (f) => <span className="text-slate-500 tabular-nums">{fmtDate(f.effective_date)}</span>,
    },
  ];

  const contribPct = (cat, mc) => {
    const r = (contrib.rates || []).find(x => x.category === cat && x.means_category === mc);
    return r ? `${Number(r.client_pct)}%` : null;
  };
  const contribColumns = [
    {
      key: 'label', header: 'Service category', sortable: false,
      render: (r) => <span className="font-medium text-slate-800">{r.label}</span>,
    },
    ...MEANS.map((m) => ({
      key: m.key, header: m.label, align: 'right', width: 150,
      render: (r) => {
        const v = contribPct(r.cat, m.key);
        return v ? <span className="tabular-nums text-slate-700">{v}</span> : <span className="text-slate-300">—</span>;
      },
    })),
  ];

  const saveButton = (editing, addLabel) => (
    <>
      {editing ? <Save size={15} /> : <Plus size={15} />} {saving ? 'Saving…' : (editing ? 'Save changes' : addLabel)}
    </>
  );

  return (
    <div>
      {head}

      <FigureStrip>
        <Figure label="Subsidy rates" value={errors.classifications ? '—' : classifications.length} />
        <Figure label="Supplements" value={errors.supplements ? '—' : supplements.length} />
        <Figure label="Management fees" value={errors.fees ? '—' : fees.length} />
        <Figure
          label="Lifetime contribution cap"
          value={contrib.lifetimeCap ? money(contrib.lifetimeCap) : '—'}
          sub={contrib.capsAsAt ? `As at ${fmtDate(contrib.capsAsAt)}` : (usingShipped ? 'Shipped figure' : undefined)}
          tone={usingShipped ? 'amber' : 'slate'}
        />
      </FigureStrip>

      <Tabs className="mb-5" value={view} onChange={setView} ariaLabel="Rates and fees sections" tabs={[
        { id: 'yours', label: 'Your rates and fees', icon: Wallet },
        { id: 'contributions', label: 'Participant contributions', icon: Percent },
        { id: 'government', label: 'Government figures', icon: Landmark },
      ]} />

      {view === 'government' && <GovFigures />}

      {view === 'yours' && (<>
      <ProviderRules />

      <Register
        title="Subsidy rates"
        className="mb-5"
        count={classifications.length}
        countLabel={classifications.length === 1 ? 'rate' : 'rates'}
        actions={<button type="button" onClick={() => openClass(null)} className="btn-primary btn-sm"><Plus size={14} /> Add rate</button>}
        columns={classColumns}
        rows={classifications}
        rowKey="id"
        loading={loading}
        error={errors.classifications}
        onRetry={load}
        onRowClick={openClass}
        activeRowKey={showClass && editingClass ? editingClass.id : null}
        rowActions={actionsFor('classifications', openClass)}
        empty={{
          icon: Layers,
          title: 'No subsidy rates yet',
          hint: 'The national Support at Home classification budgets are already in the government figures above. Add a rate here only for a figure they do not cover, such as a legacy Home Care Package level.',
          action: <button type="button" onClick={() => openClass(null)} className="btn-primary btn-sm"><Plus size={14} /> Add rate</button>,
        }}
        footer="Support at Home classifications are paid each quarter; legacy Home Care Package levels are paid each day."
      />

      <Register
        title="Supplements"
        className="mb-5"
        count={supplements.length}
        countLabel={supplements.length === 1 ? 'supplement' : 'supplements'}
        actions={<button type="button" onClick={() => openSupp(null)} className="btn-primary btn-sm"><Plus size={14} /> Add supplement</button>}
        columns={suppColumns}
        rows={supplements}
        rowKey="id"
        loading={loading}
        error={errors.supplements}
        onRetry={load}
        onRowClick={openSupp}
        activeRowKey={showSupp && editingSupp ? editingSupp.id : null}
        rowActions={actionsFor('supplements', openSupp)}
        empty={{
          icon: Gift,
          title: 'No supplements yet',
          hint: 'Supplements are paid on top of the base subsidy for remoteness, oxygen, dementia and the like. Add the ones your participants attract.',
          action: <button type="button" onClick={() => openSupp(null)} className="btn-primary btn-sm"><Plus size={14} /> Add supplement</button>,
        }}
      />

      <Register
        title="Management fees"
        className="mb-5"
        count={fees.length}
        countLabel={fees.length === 1 ? 'fee' : 'fees'}
        actions={<button type="button" onClick={() => openFee(null)} className="btn-primary btn-sm"><Plus size={14} /> Add fee</button>}
        columns={feeColumns}
        rows={fees}
        rowKey="id"
        loading={loading}
        error={errors.fees}
        onRetry={load}
        onRowClick={openFee}
        activeRowKey={showFee && editingFee ? editingFee.id : null}
        rowActions={actionsFor('fees', openFee)}
        empty={{
          icon: Wallet,
          title: 'No management fees yet',
          hint: 'Set what you charge a Home Care Package for care management and package management, as a percentage of the subsidy or as a fixed amount.',
          action: <button type="button" onClick={() => openFee(null)} className="btn-primary btn-sm"><Plus size={14} /> Add fee</button>,
        }}
        footer="Home Care Packages only. Support at Home has no care or package management fee on the budget: care management is claimed from your pooled care management account."
      />
      </>)}

      {view === 'contributions' && (
      <Panel
        title="Participant contributions, Support at Home"
        icon={Calculator}
        bodyClass="divide-y divide-slate-100"
      >
        <div className="px-4 py-3">
          <p className="text-[13px] text-slate-600 max-w-[75ch]">
            What the participant pays towards each service category, by their means category. Clinical supports are fully
            government-funded, so a participant never contributes towards them. Part-pensioner rates are set individually by the
            Services Australia means assessment.
          </p>
        </div>
        <DataTable
          columns={contribColumns}
          rows={CONTRIB_ROWS}
          rowKey="cat"
          dense
          stickyHeader={false}
          label="Participant contribution rates"
          loading={loading}
          error={errors.contrib}
          onRetry={load}
          empty={{ icon: Calculator, title: 'No contribution rates loaded', hint: 'Reload the tab to fetch them again.' }}
          footer={`Personal care is fully government-funded from ${contrib.personalCareFreeFrom || '2026-10-01'}. A no worse off participant (on a Home Care Package on 12 September 2024) pays the no worse off rates listed under Government figures, or their protected percentage if it is lower; set that on the participant's funding plan. Lifetime caps in force now: ${contrib.lifetimeCap ? money(contrib.lifetimeCap) : '—'} standard, ${contrib.hcpLifetimeCap ? money(contrib.hcpLifetimeCap) : '—'} no worse off.`}
        />
      </Panel>
      )}

      {showClass && (
        <DetailDrawer
          open onClose={closeClass} icon={Layers} width="md"
          title={editingClass ? 'Edit subsidy rate' : 'Add a subsidy rate'}
          subtitle="What the government pays for a participant at this classification."
          footer={(
            <>
              <button type="button" onClick={closeClass} className="btn-secondary">Cancel</button>
              <button type="button" onClick={submitClass} disabled={saving} className="btn-primary">{saveButton(editingClass, 'Add rate')}</button>
            </>
          )}
        >
          <div className="space-y-4">
            <Field label="Scheme">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {['SAH', 'HCP'].map(sc => (
                  <button
                    key={sc} type="button" aria-pressed={classForm.scheme === sc}
                    onClick={() => setClassForm(f => ({ ...f, scheme: sc }))}
                    className={`px-3 py-2 rounded-lg text-[13px] border transition-colors ${classForm.scheme === sc
                      ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                      : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                  >
                    {sc === 'SAH' ? 'Support at Home' : 'Home Care Package'}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Classification or level" required>
              {(id) => (
                <input
                  id={id} className="input-field"
                  placeholder={classForm.scheme === 'SAH' ? 'Classification 4' : 'Level 2'}
                  value={classForm.level}
                  onChange={e => setClassForm(f => ({ ...f, level: e.target.value }))}
                />
              )}
            </Field>
            <FormRow cols={2}>
              <Field label="Rate" required hint={classForm.scheme === 'SAH' ? 'Dollars per quarter.' : 'Dollars per day.'}>
                {(id) => (
                  <input
                    id={id} type="number" min="0" step="0.01" className="input-field" placeholder="0.00"
                    value={classForm.rate}
                    onChange={e => setClassForm(f => ({ ...f, rate: e.target.value }))}
                  />
                )}
              </Field>
              <Field label="Effective from" hint="Services on or after this date use this rate.">
                {(id) => (
                  <input
                    id={id} type="date" className="input-field"
                    value={classForm.effective_date}
                    onChange={e => setClassForm(f => ({ ...f, effective_date: e.target.value }))}
                  />
                )}
              </Field>
            </FormRow>
          </div>
        </DetailDrawer>
      )}

      {showSupp && (
        <DetailDrawer
          open onClose={closeSupp} icon={Gift} width="md"
          title={editingSupp ? 'Edit supplement' : 'Add a supplement'}
          subtitle="Paid on top of the base subsidy."
          footer={(
            <>
              <button type="button" onClick={closeSupp} className="btn-secondary">Cancel</button>
              <button type="button" onClick={submitSupp} disabled={saving} className="btn-primary">{saveButton(editingSupp, 'Add supplement')}</button>
            </>
          )}
        >
          <div className="space-y-4">
            <Field label="Name" required>
              {(id) => (
                <>
                  <select
                    id={id} className="input-field"
                    value={SUPPLEMENT_OPTIONS.includes(suppForm.name) ? suppForm.name : '__custom'}
                    onChange={e => setSuppForm(f => ({ ...f, name: e.target.value === '__custom' ? '' : e.target.value }))}
                  >
                    {SUPPLEMENT_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    <option value="__custom">Something else</option>
                  </select>
                  {!SUPPLEMENT_OPTIONS.includes(suppForm.name) && (
                    <input
                      className="input-field mt-2"
                      aria-label="Supplement name"
                      placeholder="Name the supplement"
                      value={suppForm.name}
                      onChange={e => setSuppForm(f => ({ ...f, name: e.target.value }))}
                    />
                  )}
                </>
              )}
            </Field>
            <FormRow cols={2}>
              <Field label="Type" hint="A reduction or loading adjusts the subsidy instead of adding to it.">
                {(id) => (
                  <select
                    id={id} className="input-field"
                    value={suppForm.type}
                    onChange={e => setSuppForm(f => ({ ...f, type: e.target.value }))}
                  >
                    <option value="Supplement">Supplement</option>
                    <option value="Reduction">Reduction</option>
                    <option value="Loading">Loading</option>
                  </select>
                )}
              </Field>
              <Field label="Default amount" required hint="Dollars per day.">
                {(id) => (
                  <input
                    id={id} type="number" min="0" step="0.01" className="input-field" placeholder="0.00"
                    value={suppForm.amount}
                    onChange={e => setSuppForm(f => ({ ...f, amount: e.target.value }))}
                  />
                )}
              </Field>
            </FormRow>
            <Field label="Effective from" hint="Services on or after this date use this amount.">
              {(id) => (
                <input
                  id={id} type="date" className="input-field"
                  value={suppForm.effective_date}
                  onChange={e => setSuppForm(f => ({ ...f, effective_date: e.target.value }))}
                />
              )}
            </Field>
          </div>
        </DetailDrawer>
      )}

      {showFee && (
        <DetailDrawer
          open onClose={closeFee} icon={Calculator} width="md"
          title={editingFee ? 'Edit management fee' : 'Add a management fee'}
          subtitle="What you charge a Home Care Package for care management and package management."
          footer={(
            <>
              <button type="button" onClick={closeFee} className="btn-secondary">Cancel</button>
              <button type="button" onClick={submitFee} disabled={saving || feeIsSah} className="btn-primary">{saveButton(editingFee, 'Add fee')}</button>
            </>
          )}
        >
          <div className="space-y-4">
            <Field label="Scheme">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {['HCP', 'SAH'].map(sc => (
                  <button
                    key={sc} type="button" aria-pressed={feeForm.scheme === sc}
                    onClick={() => setFeeForm(f => ({ ...f, scheme: sc }))}
                    className={`px-3 py-2 rounded-lg text-[13px] border transition-colors ${feeForm.scheme === sc
                      ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                      : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                  >
                    {sc === 'SAH' ? 'Support at Home' : 'Home Care Package'}
                  </button>
                ))}
              </div>
            </Field>

            {/* The server refuses any Support at Home care or package management fee, so the form
                does not offer one: it says why instead of failing on save. */}
            {feeIsSah ? (
              <p className="flex items-start gap-2 rounded-lg bg-slate-50 px-3 py-2.5 text-[13px] text-slate-700">
                <Info size={15} className="mt-0.5 shrink-0 text-slate-500" aria-hidden="true" />
                <span>
                  Support at Home has no care management or package management fee on the participant&apos;s budget. Care
                  management is claimed from your pooled care management account, at the price per hour in your service
                  agreement rules above, so there is nothing to set here.
                  {editingFee ? ' If this row no longer applies, remove it from the list.' : ''}
                </span>
              </p>
            ) : (
              <>
                <Field label="Classification or level" required>
                  {(id) => (
                    <input
                      id={id} className="input-field"
                      placeholder="Level 3"
                      value={feeForm.level}
                      onChange={e => setFeeForm(f => ({ ...f, level: e.target.value }))}
                    />
                  )}
                </Field>

                <Field label="Care management" hint="Leave the amount empty if you do not charge one.">
                  {(id) => (
                    <div className="flex gap-2">
                      <ModeToggle value={feeForm.care_mode} label="Care management fee type" onChange={(m) => setFeeForm(f => ({ ...f, care_mode: m }))} />
                      <input
                        id={id} type="number" min="0" step="0.01" className="input-field flex-1"
                        placeholder={feeForm.care_mode === 'percent' ? 'Percent of the subsidy' : 'Dollars per period'}
                        value={feeForm.care_value}
                        onChange={e => setFeeForm(f => ({ ...f, care_value: e.target.value }))}
                      />
                    </div>
                  )}
                </Field>

                <Field label="Package management" hint="At least one of the two fees is required.">
                  {(id) => (
                    <div className="flex gap-2">
                      <ModeToggle value={feeForm.package_mode} label="Package management fee type" onChange={(m) => setFeeForm(f => ({ ...f, package_mode: m }))} />
                      <input
                        id={id} type="number" min="0" step="0.01" className="input-field flex-1"
                        placeholder={feeForm.package_mode === 'percent' ? 'Percent of the subsidy' : 'Dollars per period'}
                        value={feeForm.package_value}
                        onChange={e => setFeeForm(f => ({ ...f, package_value: e.target.value }))}
                      />
                    </div>
                  )}
                </Field>

                <Field label="Effective from" hint="Services on or after this date use these fees.">
                  {(id) => (
                    <input
                      id={id} type="date" className="input-field"
                      value={feeForm.effective_date}
                      onChange={e => setFeeForm(f => ({ ...f, effective_date: e.target.value }))}
                    />
                  )}
                </Field>
              </>
            )}
          </div>
        </DetailDrawer>
      )}
    </div>
  );
}
